import { $generateHtmlFromNodes } from '@lexical/html';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CharacterLimitPlugin } from '@lexical/react/LexicalCharacterLimitPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import LexicalClickableLinkPlugin from '@lexical/react/LexicalClickableLinkPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import useLexicalEditable from '@lexical/react/useLexicalEditable';
import { EditorState, LexicalEditor } from 'lexical';
import { JSX, useEffect, useState } from 'react';
import { EditorContainer } from './components/EditorContainer';
import { EditorScroller } from './components/EditorScroller';
import { tableNodes } from './nodes';
import { TablePlugin as NewTablePlugin } from './plugins/TablePlugin';

import { useSettings } from './context/SettingsContext';
import { useSharedHistoryContext } from './context/SharedHistoryContext';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import KeywordsPlugin from './plugins/KeywordsPlugin';
import LinkPlugin from './plugins/LinkPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import SpeechToTextPlugin from './plugins/SpeechToTextPlugin';
import TabFocusPlugin from './plugins/TabFocusPlugin';
import TableCellResizer from './plugins/TableCellResizer';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import ContentEditable from './ui/ContentEditable';
import Placeholder from './ui/Placeholder';
import theme from './themes/theme';

export type EditorProps = {
  onChange: (richText: string) => void;
};
export default function Editor({ onChange }: EditorProps): JSX.Element {
  const { historyState } = useSharedHistoryContext();
  const {
    settings: { isRichText, isCharLimit, isCharLimitUtf8 },
  } = useSettings();
  const isEditable = useLexicalEditable();
  const text = 'Enter some rich text...';
  const placeholder = <Placeholder>{text}</Placeholder>;
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState<boolean>(false);

  useEffect(() => {
    // editor.setEditable(false);
  }, []);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport = window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  const onChangeWrapper = (editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const htmlString = $generateHtmlFromNodes(editor);
      onChange(htmlString);
    });
  };

  const cellEditorConfig = {
    namespace: 'rte-table',
    nodes: [...tableNodes],
    onError: (error: Error) => {
      throw error;
    },
    theme,
  };

  return (
    <>
      {isRichText && <ToolbarPlugin />}
      <AutoFocusPlugin />
      <ClearEditorPlugin />
      <HashtagPlugin />
      <KeywordsPlugin />
      <SpeechToTextPlugin />
      <AutoLinkPlugin />
      <HistoryPlugin externalHistoryState={historyState} />
      <OnChangePlugin onChange={onChangeWrapper} />
      {isRichText ? (
        <RichTextPlugin
          ErrorBoundary={LexicalErrorBoundary}
          placeholder={placeholder}
          contentEditable={
            <EditorScroller>
              <EditorContainer ref={onRef}>
                <ContentEditable />
              </EditorContainer>
            </EditorScroller>
          }
        />
      ) : (
        <>
          <PlainTextPlugin
            contentEditable={<ContentEditable />}
            ErrorBoundary={LexicalErrorBoundary}
            placeholder={placeholder}
          />
          <HistoryPlugin externalHistoryState={historyState} />
        </>
      )}
      <ListPlugin />
      <CheckListPlugin />
      <ListMaxIndentLevelPlugin maxDepth={7} />
      <TableCellResizer />
      <NewTablePlugin cellEditorConfig={cellEditorConfig}>
        <AutoFocusPlugin />
        <RichTextPlugin
          contentEditable={<ContentEditable className="TableNode__contentEditable" />}
          ErrorBoundary={LexicalErrorBoundary}
          placeholder={null}
        />
        <HistoryPlugin />
        <LinkPlugin />
        <LexicalClickableLinkPlugin />
        <FloatingTextFormatToolbarPlugin />
      </NewTablePlugin>
      <LinkPlugin />
      {!isEditable && <LexicalClickableLinkPlugin />}
      <HorizontalRulePlugin />
      <TabFocusPlugin />
      <TabIndentationPlugin />
      {floatingAnchorElem && (
        <>
          <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
          <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
          <FloatingTextFormatToolbarPlugin anchorElem={floatingAnchorElem} />
        </>
      )}
      {(isCharLimit || isCharLimitUtf8) && (
        <CharacterLimitPlugin charset={isCharLimit ? 'UTF-16' : 'UTF-8'} maxLength={5} />
      )}
    </>
  );
}
